import React, { createContext, useState, useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { translations } from '../translations';

const LanguageContext = createContext();

// RTL languages: Arabic, Hebrew, Persian (fa), Urdu (ur)
const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur'];

export const isRTL = (langCode) => {
  return RTL_LANGUAGES.includes(langCode);
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const getInitialLang = (path) => {
  const segment = path.split('/').filter(Boolean)[0];
  if (segment && translations[segment]) {
    return segment;
  }
  return 'en';
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const [language, setLanguage] = useState(null);

  const currentLang = language || getInitialLang(location.pathname);

  // Sync state when route changes (so manual switch + navigation stays consistent)
  useEffect(() => {
    const nextLang = getInitialLang(location.pathname);
    if (nextLang !== language) {
      setLanguage(null); // fall back to path-derived language
    }
  }, [location.pathname, language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[currentLang];
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        return key;
      }
    }
    
    return value || key;
  };

  const switchLanguage = (lang) => {
    setLanguage(lang);
  };

  const isRtlLang = isRTL(currentLang);

  return (
    <LanguageContext.Provider value={{ language: currentLang, switchLanguage, t, isRtl: isRtlLang }}>
      {children}
    </LanguageContext.Provider>
  );
};
